import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import LoadFailureNote from "../components/LoadFailureNote";
import { PERIODS, fetchUsageReport } from "../lib/adminUsage";

const ROLE_LABEL = { player: "Players", coach: "Coaches" };
const roleLabel = (r) => ROLE_LABEL[r] ?? r;
const ROLES = ["player", "coach"];

const LOG_STEPS = [
  ["start", "Started"],
  ["ground", "Ground picked"],
  ["scorecard", "Details"],
  ["saved", "Saved"],
  ["left_at_ground", "Left at the ground"],
  ["left_at_scorecard", "Left at the details"],
];
// The closed list of arrival words the API sends (api/src/lib/usage.ts, USAGE_NAV_VIA), in its order.
const VIA_LABEL = {
  destination_row: "Destination row",
  coach_shortcut: "Coach shortcut",
  account_menu: "Account menu",
  bell: "Bell",
  back: "Back",
  in_page: "In page",
  deep_link: "Deep link",
  unknown: "Unknown",
};
const VIA_ORDER = Object.keys(VIA_LABEL);
const viaLabel = (v) => VIA_LABEL[v] ?? String(v).replace(/_/g, " ");

// "Coaches and payments": the step markers the report's `actions` rows carry (api/src/lib/usage.ts, #1594). Shown as sent, one line each.
const STEPS = [
  { name: "coach.invite_sent", item: null, label: "Coaches invited players" },
  { name: "coach.invite_accepted", item: null, label: "Invites accepted" },
  { name: "xi.picked", item: "complete", label: "Picked an XI (complete)" },
  { name: "xi.picked", item: "partial", label: "Picked an XI (partial)" },
  { name: "plan.reached", item: null, label: "Reached the plan screen" },
  { name: "plan.checkout_started", item: null, label: "Started checkout" },
  { name: "plan.return_paid", item: null, label: "Came back from checkout paid" },
  { name: "plan.return_failed", item: null, label: "Came back from checkout, payment failed" },
  { name: "billing.stop", item: null, label: "Stopped billing" },
];

const card = {
  border: "var(--nw-border-card)",
  borderRadius: "var(--nw-radius-card)",
  background: "var(--nw-surface)",
  padding: "var(--nw-space-card)",
  minWidth: 0,
};
const th = { padding: "8px 10px", fontWeight: 600, textAlign: "left" };
const td = { padding: "8px 10px", borderTop: "1px solid var(--nw-line-faint)" };

// A suppressed count is the string the API sent; show it exactly.
const show = (v) => (v === null || v === undefined ? "—" : String(v));
const num = (v) => (typeof v === "number" ? v : null);

function Block({ title, sub, sentence, testId, children, className = "" }) {
  return (
    <section style={card} className={className} data-testid={testId}>
      <h2 className="nw-h2" style={{ margin: 0 }}>{title}</h2>
      {sub && <p className="nw-help" style={{ margin: 0 }}>{sub}</p>}
      <div style={{ marginTop: "var(--nw-space-tight)" }}>{children}</div>
      {sentence && <p className="nw-body" style={{ marginTop: "var(--nw-space-tight)" }} data-testid={`${testId}-sentence`}>{sentence}</p>}
    </section>
  );
}

/** One labelled bar. The number is always written next to it, so the bar adds nothing the text lacks. */
function BarRow({ label, value, max, testId }) {
  const n = num(value);
  const pct = n !== null && max > 0 ? Math.max(2, Math.round((n / max) * 100)) : 0;
  return (
    <div className="flex items-center" style={{ gap: 10, padding: "4px 0" }} data-testid={testId}>
      <span className="nw-prose" style={{ width: 130, flexShrink: 0 }}>{label}</span>
      <span aria-hidden="true" style={{ flex: 1, minWidth: 0, height: 10, background: "var(--nw-line-faint)", borderRadius: 5 }}>
        <span style={{ display: "block", height: "100%", width: `${pct}%`, background: "var(--nw-ink)", borderRadius: 5 }} />
      </span>
      <span className="nw-caption-strong" style={{ minWidth: 76, textAlign: "right" }}>{show(value)}</span>
    </div>
  );
}

function ScrollTable({ children, testId, minWidth = 360 }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="w-full" style={{ borderCollapse: "collapse", minWidth }} data-testid={testId}>{children}</table>
    </div>
  );
}

function Head({ cols }) {
  return (
    <thead>
      <tr>{cols.map((c) => <th key={c} className="nw-label" style={th}>{c}</th>)}</tr>
    </thead>
  );
}

const lastWeekOf = (rows) => rows.reduce((m, r) => (r.week > m ? r.week : m), "");

export default function AdminUsage() {
  const [weeks, setWeeks] = useState(PERIODS[0].weeks);
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setFailed(false);
    fetchUsageReport(weeks)
      .then((data) => { if (!cancelled) setReport(data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [weeks, reloadKey]);

  return (
    <div className="nw-page-stack" data-testid="admin-usage">
      <div>
        <Link to="/admin" className="nw-caption inline-flex items-center gap-1" style={{ color: "var(--nw-ink-muted)" }}>
          <ChevronLeft size={14} /> Admin
        </Link>
        <p className="nw-eyebrow" style={{ marginTop: "var(--nw-space-tight)" }}>Admin</p>
        <h1 className="nw-title" style={{ marginTop: "var(--nw-space-xtight)" }}>Usage</h1>
        <p className="nw-lede" style={{ marginTop: "var(--nw-space-tight)", maxWidth: 640 }}>
          How people are getting in, coming back and using the app. Players and coaches only; no names.
        </p>
      </div>

      <div role="group" aria-label="Period" className="flex flex-wrap" style={{ gap: 8 }} data-testid="usage-period">
        {PERIODS.map((p) => (
          <button
            key={p.weeks}
            type="button"
            aria-pressed={weeks === p.weeks}
            onClick={() => setWeeks(p.weeks)}
            data-testid={`usage-period-${p.weeks}`}
            style={{
              minHeight: 44,
              padding: "0 16px",
              border: "1px solid var(--nw-line-strong)",
              borderRadius: "var(--nw-radius-control)",
              background: weeks === p.weeks ? "var(--nw-ink)" : "var(--nw-surface)",
              color: weeks === p.weeks ? "var(--nw-surface)" : "var(--nw-ink)",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {failed ? (
        <div style={card}>
          <LoadFailureNote what="the usage report" onRetry={() => setReloadKey((k) => k + 1)} testId="admin-usage-failed" />
        </div>
      ) : !report ? (
        <p className="nw-help" data-testid="admin-usage-loading">Loading…</p>
      ) : (
        <Report report={report} weeks={weeks} />
      )}
    </div>
  );
}

function Report({ report, weeks }) {
  const hidden = report.suppressed_roles ?? [];
  const floor = report.suppress_below;
  const hiddenText = `not shown (fewer than ${floor} accounts)`;
  const signup = report.signup_funnel ?? [];
  const logf = report.log_funnel ?? [];
  const ret = report.return ?? [];
  const actives = report.weekly_actives ?? [];
  const screens = report.screens ?? [];
  const nav = report.navigation ?? {};
  const via = nav.via ?? [];
  const actions = report.actions ?? [];

  const currentWeek = lastWeekOf(actives);
  const perRole = (pick) => ROLES.map((role) => ({ role, text: hidden.includes(role) ? hiddenText : pick(role) }));

  const headlines = [
    {
      id: "new",
      label: "New this week",
      note: "Accounts that signed up",
      rows: perRole((role) => show(signup.find((r) => r.week === currentWeek && r.role === role)?.signed_up)),
    },
    {
      id: "active",
      label: "Active",
      note: "Used the app this week",
      rows: perRole((role) => show(actives.find((r) => r.week === currentWeek && r.role === role)?.accounts)),
    },
    {
      id: "back",
      label: "Came back",
      note: "Active again the week after signing up (latest sign-up week that has one)",
      rows: perRole((role) => {
        const done = ret.filter((r) => r.role === role && r.week_1 !== null);
        const row = done[done.length - 1];
        return row ? show(row.week_1.accounts) : "—";
      }),
    },
  ];

  const logRoles = ROLES.filter((r) => !hidden.includes(r) && logf.some((l) => l.role === r));
  const startedOf = (role) => logf.find((l) => l.role === role && l.step === "start")?.accounts;

  // One plain sentence per block, built only from values the API sent.
  const lastSignup = [...signup].reverse().find((r) => !hidden.includes(r.role));
  const gettingInSentence = lastSignup
    ? `${roleLabel(lastSignup.role)} who signed up in ${lastSignup.week}: ${show(lastSignup.signed_up)} signed up, ${show(lastSignup.onboarded)} finished onboarding, ${show(lastSignup.first_log)} logged a first match.`
    : null;
  const logRole = logRoles[0];
  const logSentence = logRole
    ? `${roleLabel(logRole)}: ${show(startedOf(logRole))} started a match and ${show(logf.find((l) => l.role === logRole && l.step === "saved")?.accounts)} saved one.`
    : null;
  const lastReturn = [...ret].reverse().find((r) => r.week_1 !== null && !hidden.includes(r.role));
  const returnSentence = lastReturn
    ? `${roleLabel(lastReturn.role)} who signed up in ${lastReturn.cohort_week}: ${show(lastReturn.new_accounts)} signed up, ${show(lastReturn.week_1.accounts)} were back the next week.`
    : null;

  const weeksShown = [...new Set(actives.map((a) => a.week))].sort().slice(-Math.max(weeks, 4));
  const activeMax = Math.max(0, ...actives.filter((a) => weeksShown.includes(a.week)).map((a) => num(a.accounts) ?? 0));

  const signupMax = Math.max(0, ...signup.map((s) => num(s.signed_up) ?? 0));
  // One item per `screens` row (screen, tab, role): the API splits a tabbed screen by tab and a screen's
  // distinct accounts cannot be added across tabs, so the per-tab rows are shown as they come.
  const tiles = [...screens].sort((a, b) => (num(b.accounts) ?? -1) - (num(a.accounts) ?? -1));
  const tileName = (t) => (t.tab ? `${t.screen} › ${t.tab}` : t.screen);
  const stepRows = STEPS.map((s) => ({ ...s, cells: ROLES.map((role) => actions.find((a) => a.name === s.name && (a.item ?? null) === s.item && a.role === role)) }));
  const tileMax = Math.max(1, ...tiles.map((t) => num(t.accounts) ?? 0));
  const viaKeys = [...new Set(via.map((v) => v.via))].sort((a, b) => (VIA_ORDER.indexOf(a) + 1 || 99) - (VIA_ORDER.indexOf(b) + 1 || 99));
  const viaRoles = [...new Set(via.map((v) => v.role))];

  const empty = signup.length === 0 && logRoles.length === 0 && ret.length === 0 && screens.length === 0;

  return (
    <div className="flex flex-col" style={{ gap: "var(--nw-space-section, 16px)" }} data-testid="admin-usage-report">
      {empty && (
        <p className="nw-body" data-testid="admin-usage-empty">Nothing has been recorded for this period yet.</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: "var(--nw-space-inline, 12px)" }} data-testid="usage-headlines">
        {headlines.map((h) => (
          <div key={h.id} style={card} data-testid={`usage-headline-${h.id}`}>
            <p className="nw-label" style={{ margin: 0 }}>{h.label}</p>
            {h.rows.map((r) => (
              <p key={r.role} className="nw-body" style={{ margin: "2px 0 0" }}>
                <span className="nw-caption">{roleLabel(r.role)}: </span>
                <strong data-testid={`usage-headline-${h.id}-${r.role}`}>{r.text}</strong>
              </p>
            ))}
            <p className="nw-help" style={{ margin: "4px 0 0" }}>{h.note}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: "var(--nw-space-inline, 12px)" }}>
        <Block title="Getting in" sub="From sign-up to a first match, by sign-up week" sentence={gettingInSentence} testId="usage-getting-in">
          {signup.length === 0 ? (
            <p className="nw-help" data-testid="usage-getting-in-empty">Nobody signed up in this period.</p>
          ) : (
            signup.map((s) => (
              <div key={`${s.week}-${s.role}`} style={{ marginBottom: 8 }} data-testid="usage-signup-row">
                <p className="nw-caption-strong" style={{ margin: 0 }}>{s.week} · {roleLabel(s.role)}</p>
                <BarRow label="Signed up" value={s.signed_up} max={signupMax} />
                <BarRow label="Finished onboarding" value={s.onboarded} max={signupMax} />
                <BarRow label="Logged a first match" value={s.first_log} max={signupMax} />
              </div>
            ))
          )}
        </Block>

        <Block title="Log a match" sub="From opening to saving, over the whole period" sentence={logSentence} testId="usage-log-match">
          {logRoles.length === 0 ? (
            <p className="nw-help" data-testid="usage-log-empty">No match logging to show for this period.</p>
          ) : (
            logRoles.map((role) => {
              const rows = LOG_STEPS.map(([step, label]) => [label, logf.find((l) => l.role === role && l.step === step)]).filter(([, r]) => r);
              const max = Math.max(0, ...rows.map(([, r]) => num(r.accounts) ?? 0));
              return (
                <div key={role} style={{ marginBottom: 8 }} data-testid="usage-log-role">
                  <p className="nw-caption-strong" style={{ margin: 0 }}>{roleLabel(role)}</p>
                  {rows.map(([label, r]) => <BarRow key={label} label={label} value={r.accounts} max={max} />)}
                </div>
              );
            })
          )}
        </Block>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: "var(--nw-space-inline, 12px)" }}>
        <Block title="Coming back" sub="Accounts, not percentages" sentence={returnSentence} testId="usage-coming-back">
          {ret.length === 0 ? (
            <p className="nw-help" data-testid="usage-return-empty">No sign-ups to follow in this period.</p>
          ) : (
            <ScrollTable testId="usage-return-table" minWidth={420}>
              <Head cols={["Signed up", "Role", "Accounts", "Back in week 2", "Week 3", "Week 4"]} />
              <tbody>
                {ret.map((r) => (
                  <tr key={`${r.cohort_week}-${r.role}`} data-testid="usage-return-row">
                    <td className="nw-prose" style={td}>{r.cohort_week}</td>
                    <td className="nw-prose" style={td}>{roleLabel(r.role)}</td>
                    <td className="nw-prose" style={td}>{show(r.new_accounts)}</td>
                    {[r.week_1, r.week_2, r.week_3].map((w, i) => (
                      <td key={i} className="nw-prose" style={td}>{w ? show(w.accounts) : "not yet"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
          )}
        </Block>

        <Block title="Active each week" sub="Accounts that used the app, by role" testId="usage-active">
          {weeksShown.length === 0 ? (
            <p className="nw-help">No weekly figures.</p>
          ) : (
            weeksShown.map((w) => (
              <div key={w} style={{ marginBottom: 6 }} data-testid="usage-active-week">
                <p className="nw-caption-strong" style={{ margin: 0 }}>{w}</p>
                {ROLES.map((role) => {
                  const cell = actives.find((a) => a.week === w && a.role === role);
                  return cell ? <BarRow key={role} label={roleLabel(role)} value={cell.accounts} max={activeMax} /> : null;
                })}
              </div>
            ))
          )}
        </Block>
      </div>

      <Block
        title="Where people go"
        sub="Screens by the accounts that reached them, one per screen, tab and role. On a phone this is a ranked list."
        testId="usage-map"
      >
        {tiles.length === 0 ? (
          <p className="nw-help" data-testid="usage-map-empty">No screens reached in this period.</p>
        ) : (
          <>
            <div className="hidden lg:flex flex-wrap" style={{ gap: 8, alignItems: "flex-end" }} data-testid="usage-map-tiles">
              {tiles.map((t) => {
                const n = num(t.accounts);
                const size = 112 + Math.round(((n ?? 0) / tileMax) * 96);
                return (
                  <div key={`${t.screen}-${t.tab}-${t.role}`} data-testid="usage-map-tile" style={{ width: size, minHeight: size, boxSizing: "border-box", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-card)", padding: 8, background: "var(--nw-surface)" }}>
                    <p className="nw-caption-strong" style={{ margin: 0, fontSize: 16 }}>{show(t.accounts)}</p>
                    <p className="nw-caption" style={{ margin: 0, wordBreak: "break-word" }}>{tileName(t)}</p>
                    <p className="nw-help" style={{ margin: 0 }}>{roleLabel(t.role)}</p>
                  </div>
                );
              })}
            </div>
            <ol className="lg:hidden" style={{ margin: 0, paddingLeft: 0, listStyle: "none" }} data-testid="usage-map-list">
              {tiles.map((t, i) => (
                <li key={`${t.screen}-${t.tab}-${t.role}`} className="flex items-baseline justify-between" style={{ gap: 8, padding: "8px 0", borderTop: i ? "1px solid var(--nw-line-faint)" : "none" }} data-testid="usage-map-item">
                  <span className="nw-prose" style={{ minWidth: 0, wordBreak: "break-word" }}>{i + 1}. {tileName(t)} <span className="nw-help">({roleLabel(t.role)})</span></span>
                  <span className="nw-caption-strong" style={{ flexShrink: 0 }}>{show(t.accounts)} accounts</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </Block>

      <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: "var(--nw-space-inline, 12px)" }}>
        <Block title="Screens reached" sub="Views and accounts, over the whole period" testId="usage-screens">
          {screens.length === 0 ? (
            <p className="nw-help" data-testid="usage-screens-empty">No screens reached in this period.</p>
          ) : (
            <ScrollTable testId="usage-screens-table" minWidth={420}>
              <Head cols={["Screen", "Tab", "Role", "Views", "Accounts"]} />
              <tbody>
                {screens.map((s, i) => (
                  <tr key={i} data-testid="usage-screens-row">
                    <td className="nw-prose" style={td}>{s.screen}</td>
                    <td className="nw-prose" style={td}>{s.tab ?? "—"}</td>
                    <td className="nw-prose" style={td}>{roleLabel(s.role)}</td>
                    <td className="nw-prose" style={td}>{show(s.views)}</td>
                    <td className="nw-prose" style={td}>{show(s.accounts)}</td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
          )}
        </Block>

        <Block title="How they got there" sub="Screen views by how the person arrived" testId="usage-via">
          {via.length === 0 ? (
            <p className="nw-help" data-testid="usage-via-empty">No arrivals recorded in this period.</p>
          ) : (
            <ScrollTable testId="usage-via-table" minWidth={360}>
              <Head cols={["Role", ...viaKeys.map(viaLabel)]} />
              <tbody>
                {viaRoles.map((role) => (
                  <tr key={role} data-testid="usage-via-row">
                    <td className="nw-prose" style={td}>{roleLabel(role)}</td>
                    {viaKeys.map((k) => (
                      <td key={k} className="nw-prose" style={td}>{show(via.find((v) => v.role === role && v.via === k)?.views)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
          )}
        </Block>
      </div>

      <Block title="Coaches and payments" sub="Accounts that reached each step, over the whole period" testId="usage-steps">
        {actions.length === 0 ? (
          <p className="nw-help" data-testid="usage-steps-empty">No steps recorded in this period.</p>
        ) : (
          <>
            <ScrollTable testId="usage-steps-table" minWidth={360}>
              <Head cols={["Step", ...ROLES.map(roleLabel)]} />
              <tbody>
                {stepRows.map((s) => (
                  <tr key={`${s.name}-${s.item}`} data-testid="usage-steps-row">
                    <td className="nw-prose" style={td}>{s.label}</td>
                    {s.cells.map((c, i) => (
                      <td key={ROLES[i]} className="nw-prose" style={td}>{show(c?.accounts)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
            <p className="nw-help" style={{ marginTop: "var(--nw-space-tight)" }}>Payments here are steps people took. Stripe stays the record of money.</p>
          </>
        )}
      </Block>

      <p className="nw-help" data-testid="usage-footnote">
        {report.exact_counts
          ? "Exact counts during the pilot. "
          : `A count under ${floor} accounts shows as "fewer than ${floor}". `}
        Random IDs per account, never shown; admins are not counted.
      </p>
    </div>
  );
}
